import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import {
  Heading,
  Section,
  Tile,
  Button,
  Tag,
  TextArea,
  FileUploader,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell
} from '@carbon/react';
import {
  ArrowLeft,
  Medication,
  Warning,
  ChartLine,
  DocumentPdf,
  Add,
  ChevronDown,
  ChevronUp,
  DocumentBlank,
  UserAvatar
} from '@carbon/icons-react';
import PageContainer from '../components/PageContainer';

const ResidentHealth = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'g100';
  
  const [newNote, setNewNote] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    diagnoses: false,
    therapy: false,
    allergies: false,
    vitalSigns: false,
    notes: false,
    documents: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Mock data - replace with real API data
  const healthData = {
    resident: {
      firstName: 'Milica',
      lastName: 'Jovanović',
      room: '101'
    },
    diagnoses: [
      { id: 1, name: 'Diabetes mellitus tip 2', date: '15.03.2020', status: 'Aktivna' },
      { id: 2, name: 'Hipertenzija', date: '10.01.2018', status: 'Aktivna' },
      { id: 3, name: 'Osteoporoza', date: '22.05.2019', status: 'Aktivna' }
    ],
    therapy: [
      { id: 1, medicine: 'Metformin', dose: '500mg', frequency: '2x dnevno', time: '08:00, 20:00', prescribedBy: 'Dr Petrović' },
      { id: 2, medicine: 'Enalapril', dose: '10mg', frequency: '1x dnevno', time: '08:00', prescribedBy: 'Dr Petrović' },
      { id: 3, medicine: 'Kalcijum + D3', dose: '1 tableta', frequency: '1x dnevno', time: '12:00', prescribedBy: 'Dr Nikolić' }
    ],
    allergies: [
      { id: 1, allergen: 'Penicilin', reaction: 'Osip na koži', severity: 'Visoka' },
      { id: 2, allergen: 'Kikiriki', reaction: 'Otežano disanje', severity: 'Visoka' }
    ],
    vitalSigns: {
      bloodPressure: [
        { date: '01.11.2025', value: '130/85', time: '08:00' },
        { date: '02.11.2025', value: '125/80', time: '08:00' },
        { date: '03.11.2025', value: '128/82', time: '08:00' },
        { date: '04.11.2025', value: '132/86', time: '08:00' },
        { date: '05.11.2025', value: '127/81', time: '08:00' }
      ],
      pulse: [
        { date: '01.11.2025', value: 72, time: '08:00' },
        { date: '02.11.2025', value: 68, time: '08:00' },
        { date: '03.11.2025', value: 70, time: '08:00' },
        { date: '04.11.2025', value: 74, time: '08:00' },
        { date: '05.11.2025', value: 69, time: '08:00' }
      ],
      glucose: [
        { date: '01.11.2025', value: 6.2, time: '08:00' },
        { date: '02.11.2025', value: 5.8, time: '08:00' },
        { date: '03.11.2025', value: 6.5, time: '08:00' },
        { date: '04.11.2025', value: 6.0, time: '08:00' },
        { date: '05.11.2025', value: 5.9, time: '08:00' }
      ],
      temperature: [
        { date: '01.11.2025', value: 36.6, time: '08:00' },
        { date: '02.11.2025', value: 36.5, time: '08:00' },
        { date: '03.11.2025', value: 36.7, time: '08:00' },
        { date: '04.11.2025', value: 36.6, time: '08:00' },
        { date: '05.11.2025', value: 36.5, time: '08:00' }
      ]
    },
    medicalNotes: [
      { id: 1, date: '05.11.2025 10:30', author: 'Dr Petrović', type: 'Lekar', note: 'Redovan pregled. Šećer u krvi u granicama normale. Nastaviti sa terapijom.' },
      { id: 2, date: '03.11.2025 14:00', author: 'Sestra Ana', type: 'Sestra', note: 'Pacijent se oseća dobro. Uzela sve lekove na vreme.' },
      { id: 3, date: '01.11.2025 09:00', author: 'Dr Nikolić', type: 'Lekar', note: 'Kontrola pritiska - u granicama normale. Preporuka za povećano unošenje tečnosti.' }
    ],
    documents: [
      { id: 1, name: 'Laboratorijski nalazi - 30.10.2025.pdf', date: '30.10.2025', type: 'Laboratorija', size: '245 KB' },
      { id: 2, name: 'RTG grudnog koša.pdf', date: '15.10.2025', type: 'Radiologija', size: '1.2 MB' },
      { id: 3, name: 'Kardiološki nalaz.pdf', date: '05.10.2025', type: 'Kardiologija', size: '187 KB' }
    ]
  };

  const getSeverityColor = (severity) => {
    if (severity === 'Visoka') return 'red';
    if (severity === 'Srednja') return 'orange';
    return 'green';
  };

  return (
    <PageContainer>
      {/* Header sa Nazad button-om i profilom */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={ArrowLeft}
          hasIconOnly
          iconDescription="Nazad"
          onClick={() => navigate(`/residents/${id}`)}
        />
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.75rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
          borderRadius: '4px',
          border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onClick={() => navigate(`/residents/${id}`)}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '4px',
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: 'rgba(159, 183, 70, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <UserAvatar size={20} style={{ color: '#9fb746' }} />
          </div>
          <div>
            <div style={{ 
              fontSize: '0.875rem', 
              fontWeight: '600',
              color: isDarkMode ? '#f4f4f4' : 'inherit'
            }}>
              {healthData.resident.firstName} {healthData.resident.lastName}
            </div>
            <div style={{ 
              fontSize: '0.7rem', 
              opacity: 0.7,
              color: isDarkMode ? '#d0d0d0' : 'inherit'
            }}>
              Soba {healthData.resident.room}
            </div>
          </div>
        </div>
      </div>

      {/* Dijagnoze */}
      <Section style={{ marginBottom: '1.5rem' }}>
        <Tile style={{ padding: '0', boxShadow: '0 2px 8px rgba(159, 183, 70, 0.15)' }}>
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '0.75rem',
              borderBottom: expandedSections.diagnoses ? '1px solid rgba(128,128,128,0.1)' : 'none',
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('diagnoses')}
          >
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
              <Medication size={20} style={{ color: '#9fb746' }} />
              Dijagnoze
            </h3>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              iconDescription={expandedSections.diagnoses ? "Zatvori" : "Otvori"}
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('diagnoses');
              }}
            >
              {expandedSections.diagnoses ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </Button>
          </div>
          
          <div style={{
            maxHeight: expandedSections.diagnoses ? '2000px' : '0',
            overflow: 'hidden',
            transition: expandedSections.diagnoses 
              ? 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out' 
              : 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease-in-out',
            opacity: expandedSections.diagnoses ? 1 : 0
          }}>
            <div style={{ padding: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {healthData.diagnoses.map(diagnosis => (
                  <div
                    key={diagnosis.id}
                    style={{
                      padding: '0.75rem',
                      border: '1px solid rgba(159, 183, 70, 0.2)',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: 'rgba(159, 183, 70, 0.02)'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '500', marginBottom: '0.25rem', fontSize: '0.875rem' }}>{diagnosis.name}</div>
                      <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                        Dijagnostikovano: {diagnosis.date}
                      </div>
                    </div>
                    <Tag type="green" size="sm">{diagnosis.status}</Tag>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Tile>
      </Section>

      {/* Trenutna terapija */}
      <Section style={{ marginBottom: '1.5rem' }}>
        <Tile style={{ padding: '0', boxShadow: '0 2px 8px rgba(159, 183, 70, 0.15)' }}>
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '0.75rem',
              borderBottom: expandedSections.therapy ? '1px solid rgba(128,128,128,0.1)' : 'none',
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('therapy')}
          >
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
              <Medication size={20} style={{ color: '#9fb746' }} />
              Trenutna terapija
            </h3>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              iconDescription={expandedSections.therapy ? "Zatvori" : "Otvori"}
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('therapy');
              }}
            >
              {expandedSections.therapy ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </Button>
          </div>
          
          <div style={{
            maxHeight: expandedSections.therapy ? '2000px' : '0',
            overflow: 'hidden',
            transition: expandedSections.therapy 
              ? 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out' 
              : 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease-in-out',
            opacity: expandedSections.therapy ? 1 : 0
          }}>
            <div style={{ padding: '0.75rem' }}>
              <DataTable rows={healthData.therapy} headers={[
                { key: 'medicine', header: 'Lek' },
                { key: 'dose', header: 'Doza' },
                { key: 'frequency', header: 'Učestalost' },
                { key: 'time', header: 'Vreme' },
                { key: 'prescribedBy', header: 'Propisao' }
              ]}>
                {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                  <Table {...getTableProps()} size="sm">
                    <TableHead>
                      <TableRow>
                        {headers.map((header) => (
                          <TableHeader {...getHeaderProps({ header })}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow {...getRowProps({ row })}>
                          {row.cells.map((cell) => (
                            <TableCell key={cell.id}>{cell.value}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </DataTable>
            </div>
          </div>
        </Tile>
      </Section>

      {/* Alergije */}
      <Section style={{ marginBottom: '1.5rem' }}>
        <Tile style={{ padding: '0', boxShadow: '0 2px 8px rgba(159, 183, 70, 0.15)' }}>
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '0.75rem',
              borderBottom: expandedSections.allergies ? '1px solid rgba(128,128,128,0.1)' : 'none',
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('allergies')}
          >
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
              <Warning size={20} style={{ color: '#da1e28' }} />
              Alergije
            </h3>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              iconDescription={expandedSections.allergies ? "Zatvori" : "Otvori"}
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('allergies');
              }}
            >
              {expandedSections.allergies ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </Button>
          </div>
          
          <div style={{
            maxHeight: expandedSections.allergies ? '2000px' : '0',
            overflow: 'hidden',
            transition: expandedSections.allergies 
              ? 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out' 
              : 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease-in-out',
            opacity: expandedSections.allergies ? 1 : 0
          }}>
            <div style={{ padding: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {healthData.allergies.map(allergy => (
                  <div
                    key={allergy.id}
                    style={{
                      padding: '0.75rem',
                      border: '2px solid rgba(218, 30, 40, 0.3)',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(218, 30, 40, 0.05)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                      <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#da1e28' }}>
                        {allergy.allergen}
                      </div>
                      <Tag type={getSeverityColor(allergy.severity)} size="sm">
                        {allergy.severity}
                      </Tag>
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                      Reakcija: {allergy.reaction}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Tile>
      </Section>

      {/* Vitalni znaci */}
      <Section style={{ marginBottom: '1.5rem' }}>
        <Tile style={{ padding: '0', boxShadow: '0 2px 8px rgba(159, 183, 70, 0.15)' }}>
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '0.75rem',
              borderBottom: expandedSections.vitalSigns ? '1px solid rgba(128,128,128,0.1)' : 'none',
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('vitalSigns')}
          >
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
              <ChartLine size={20} style={{ color: '#9fb746' }} />
              Vitalni znaci
            </h3>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              iconDescription={expandedSections.vitalSigns ? "Zatvori" : "Otvori"}
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('vitalSigns');
              }}
            >
              {expandedSections.vitalSigns ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </Button>
          </div>
          
          <div style={{
            maxHeight: expandedSections.vitalSigns ? '2000px' : '0',
            overflow: 'hidden',
            transition: expandedSections.vitalSigns 
              ? 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out' 
              : 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease-in-out',
            opacity: expandedSections.vitalSigns ? 1 : 0
          }}>
            <div style={{ padding: '0.75rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                {/* Krvni pritisak */}
                <div style={{ padding: '0.75rem', border: '1px solid rgba(159, 183, 70, 0.2)', borderRadius: '4px' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.8125rem', fontWeight: '600' }}>
                    Krvni pritisak
                  </h4>
                  {healthData.vitalSigns.bloodPressure.slice(0, 3).map((reading, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      padding: '0.35rem 0',
                      borderBottom: index < 2 ? '1px solid rgba(0,0,0,0.1)' : 'none'
                    }}>
                      <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>{reading.date}</span>
                      <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>{reading.value}</span>
                    </div>
                  ))}
                </div>

                {/* Puls */}
                <div style={{ padding: '0.75rem', border: '1px solid rgba(159, 183, 70, 0.2)', borderRadius: '4px' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.8125rem', fontWeight: '600' }}>
                    Puls
                  </h4>
                  {healthData.vitalSigns.pulse.slice(0, 3).map((reading, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      padding: '0.35rem 0',
                      borderBottom: index < 2 ? '1px solid rgba(0,0,0,0.1)' : 'none'
                    }}>
                      <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>{reading.date}</span>
                      <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>{reading.value}</span>
                    </div>
                  ))}
                </div>

                {/* Šećer */}
                <div style={{ padding: '0.75rem', border: '1px solid rgba(159, 183, 70, 0.2)', borderRadius: '4px' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.8125rem', fontWeight: '600' }}>
                    Šećer u krvi
                  </h4>
                  {healthData.vitalSigns.glucose.slice(0, 3).map((reading, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      padding: '0.35rem 0',
                      borderBottom: index < 2 ? '1px solid rgba(0,0,0,0.1)' : 'none'
                    }}>
                      <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>{reading.date}</span>
                      <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>{reading.value}</span>
                    </div>
                  ))}
                </div>

                {/* Temperatura */}
                <div style={{ padding: '0.75rem', border: '1px solid rgba(159, 183, 70, 0.2)', borderRadius: '4px' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.8125rem', fontWeight: '600' }}>
                    Temperatura
                  </h4>
                  {healthData.vitalSigns.temperature.slice(0, 3).map((reading, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      padding: '0.35rem 0',
                      borderBottom: index < 2 ? '1px solid rgba(0,0,0,0.1)' : 'none'
                    }}>
                      <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>{reading.date}</span>
                      <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>{reading.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Tile>
      </Section>

      {/* Napomene lekara i sestrinska zapažanja */}
      <Section style={{ marginBottom: '1.5rem' }}>
        <Tile style={{ padding: '0', boxShadow: '0 2px 8px rgba(159, 183, 70, 0.15)' }}>
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '0.75rem',
              borderBottom: expandedSections.notes ? '1px solid rgba(128,128,128,0.1)' : 'none',
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('notes')}
          >
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
              <DocumentBlank size={20} style={{ color: '#9fb746' }} />
              Napomene
            </h3>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              iconDescription={expandedSections.notes ? "Zatvori" : "Otvori"}
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('notes');
              }}
            >
              {expandedSections.notes ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </Button>
          </div>
          
          <div style={{
            maxHeight: expandedSections.notes ? '2000px' : '0',
            overflow: 'hidden',
            transition: expandedSections.notes 
              ? 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out' 
              : 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease-in-out',
            opacity: expandedSections.notes ? 1 : 0
          }}>
            <div style={{ padding: '0.75rem' }}>
              {/* Nova napomena */}
              <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'rgba(159, 183, 70, 0.05)', borderRadius: '4px' }}>
                <TextArea
                  labelText="Dodaj napomenu"
                  placeholder="Unesite napomenu..."
                  rows={2}
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                />
                <Button size="sm" style={{ marginTop: '0.5rem' }}>
                  Sačuvaj
                </Button>
              </div>

              {/* Lista napomena */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {healthData.medicalNotes.map(note => (
                  <div
                    key={note.id}
                    style={{
                      padding: '0.75rem',
                      border: '1px solid rgba(159, 183, 70, 0.2)',
                      borderRadius: '4px',
                      backgroundColor: note.type === 'Lekar' ? 'rgba(159, 183, 70, 0.03)' : 'rgba(0, 0, 0, 0.02)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <div>
                        <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{note.author}</span>
                        <Tag type={note.type === 'Lekar' ? 'blue' : 'green'} size="sm" style={{ marginLeft: '0.5rem' }}>
                          {note.type}
                        </Tag>
                      </div>
                      <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>{note.date}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.8125rem', lineHeight: '1.4' }}>{note.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Tile>
      </Section>

      {/* Upload nalaza */}
      <Section style={{ marginBottom: '1.5rem' }}>
        <Tile style={{ padding: '0', boxShadow: '0 2px 8px rgba(159, 183, 70, 0.15)' }}>
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '0.75rem',
              borderBottom: expandedSections.documents ? '1px solid rgba(128,128,128,0.1)' : 'none',
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('documents')}
          >
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
              <DocumentPdf size={20} style={{ color: '#9fb746' }} />
              Dokumentacija
            </h3>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              iconDescription={expandedSections.documents ? "Zatvori" : "Otvori"}
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('documents');
              }}
            >
              {expandedSections.documents ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </Button>
          </div>
          
          <div style={{
            maxHeight: expandedSections.documents ? '2000px' : '0',
            overflow: 'hidden',
            transition: expandedSections.documents 
              ? 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out' 
              : 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease-in-out',
            opacity: expandedSections.documents ? 1 : 0
          }}>
            <div style={{ padding: '0.75rem' }}>
              {/* File uploader */}
              <div style={{ marginBottom: '1rem' }}>
                <FileUploader
                  labelTitle="Dodaj dokument"
                  labelDescription="Max 10MB. PDF, JPG, PNG"
                  buttonLabel="Izaberi fajl"
                  filenameStatus="edit"
                  accept={['.pdf', '.jpg', '.jpeg', '.png']}
                  multiple
                  size="sm"
                />
              </div>

              {/* Lista dokumenata */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {healthData.documents.map(doc => (
                  <div
                    key={doc.id}
                    style={{
                      padding: '0.75rem',
                      border: '1px solid rgba(159, 183, 70, 0.2)',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: 'rgba(159, 183, 70, 0.02)',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(159, 183, 70, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(159, 183, 70, 0.02)';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <DocumentPdf size={24} style={{ color: '#da1e28' }} />
                      <div>
                        <div style={{ fontWeight: '500', marginBottom: '0.25rem', fontSize: '0.875rem' }}>{doc.name}</div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                          {doc.type} • {doc.date}
                        </div>
                      </div>
                    </div>
                    <Button size="sm" kind="ghost">
                      Preuzmi
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Tile>
      </Section>
    </PageContainer>
  );
};

export default ResidentHealth;
